import { Link } from "react-router-dom";
import { FadeUp, Ribbon, ImageCard, ButtonPrimary, ButtonGhost } from "../components/Reveal";
import { WorkAreaSection } from "../components/WorkAreaSection";
import { useContent, imgSrc } from "../context/ContentContext";
import { images } from "../config/siteConfig";

const Hero = ({ site }) => (
  <section data-testid="hero-section" className="bg-[#111111] text-white">
    <div className="mx-auto grid max-w-[1400px] lg:min-h-[560px] lg:grid-cols-5">
      <div className="order-2 flex flex-col justify-center px-4 py-12 sm:px-6 lg:order-1 lg:col-span-3 lg:py-20 lg:pr-16">
        <FadeUp>
          <p data-testid="hero-label" className="font-display text-xs font-bold uppercase tracking-[0.2em] text-cream">
            {site.heroLabel}
          </p>
        </FadeUp>
        <FadeUp delay={0.05}>
          <h1
            data-testid="hero-headline"
            className="mt-4 font-display text-4xl font-bold uppercase leading-[0.95] tracking-wide sm:text-5xl lg:text-6xl"
          >
            {site.heroTitle1}
            <br />
            <span className="text-cream">{site.heroTitle2}</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p data-testid="hero-subtext" className="mt-6 max-w-xl text-[15px] leading-relaxed text-white/70">
            {site.heroSubtext}
          </p>
        </FadeUp>
        <FadeUp delay={0.15} className="mt-8 flex flex-wrap gap-3">
          <ButtonPrimary to="/contact" testid="hero-offerte-cta">
            Vraag een offerte aan
          </ButtonPrimary>
          <Link
            to="/portfolio"
            data-testid="hero-portfolio-cta"
            className="inline-block rounded-[4px] border border-white/40 px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:border-cream hover:bg-cream hover:text-[#111111]"
          >
            Bekijk mijn werk
          </Link>
        </FadeUp>
      </div>
      <div className="relative order-1 lg:order-2 lg:col-span-2">
        <img
          src={imgSrc(site.heroImage || images.hero.src)}
          alt={images.hero.alt}
          className="h-[360px] w-full object-cover object-top sm:h-[480px] lg:absolute lg:inset-0 lg:h-full"
          loading="eager"
        />
        <Ribbon className="absolute bottom-6 left-4 whitespace-nowrap">{site.heroRibbon}</Ribbon>
      </div>
    </div>
  </section>
);

const Intro = ({ home }) => (
  <section data-testid="intro-section" className="bg-white">
    <div className="mx-auto max-w-[1400px] px-4 py-12 sm:px-6 sm:py-16">
      <FadeUp>
        <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-[#8E8D8A]">{home.introEyebrow}</p>
      </FadeUp>
      <FadeUp delay={0.05}>
        <h2
          data-testid="intro-headline"
          className="mt-3 max-w-3xl font-display text-3xl font-bold uppercase leading-tight tracking-wide text-[#111111] sm:text-4xl"
        >
          {home.introTitle}
        </h2>
      </FadeUp>
      <FadeUp delay={0.1}>
        <p className="mt-5 max-w-3xl text-[15px] leading-relaxed text-[#555555]">{home.introText}</p>
      </FadeUp>
      <FadeUp delay={0.15} className="mt-8 flex flex-wrap gap-3">
        <ButtonGhost to="/diensten/timmerwerken" testid="hero-diensten-cta">
          Bekijk mijn diensten
        </ButtonGhost>
      </FadeUp>
    </div>
  </section>
);

const ServicesGrid = ({ services, projects }) => (
  <section data-testid="services-overview" className="bg-white">
    <div className="mx-auto max-w-[1400px] px-4 pb-14 sm:px-6">
      <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2">
        {services.map((s) => (
          <FadeUp key={s.slug}>
            <ImageCard to={`/diensten/${s.slug}`} image={s.image} label={s.title} testid={`service-card-${s.slug}`} />
          </FadeUp>
        ))}
        <FadeUp>
          <ImageCard
            to="/portfolio"
            image={projects[0]?.image || images.houtskeletbouw}
            label="Portfolio"
            testid="service-card-portfolio"
          />
        </FadeUp>
      </div>
    </div>
  </section>
);

const AboutBand = ({ home }) => (
  <section data-testid="about-band" className="bg-[#EFEDE8]">
    <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <FadeUp>
          <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-[#111111] sm:text-3xl">{home.aboutTitle1}</h2>
        </FadeUp>
        <FadeUp delay={0.05}>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[#555555]">{home.aboutText1}</p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-10 font-display text-2xl font-bold uppercase tracking-wide text-[#111111] sm:text-3xl">{home.aboutTitle2}</h2>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[#555555]">{home.aboutText2}</p>
        </FadeUp>
      </div>
      <FadeUp delay={0.1}>
        <div className="self-start border border-[#DDD9D0] bg-white p-6 shadow-[0_10px_30px_-16px_rgba(0,0,0,0.25)]">
          <h3 className="text-center font-display text-xl font-bold uppercase tracking-wide text-[#111111]">{home.ctaTitle}</h3>
          <p className="mt-3 text-center text-sm leading-relaxed text-[#555555]">{home.ctaText}</p>
          <div className="mt-5 text-center">
            <ButtonPrimary to="/contact" testid="about-band-offerte-button">
              Vraag een offerte aan
            </ButtonPrimary>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  const { site, pages, services, projects } = useContent();
  return (
    <main data-testid="home-page">
      <Hero site={site} />
      <Intro home={pages.home} />
      <ServicesGrid services={services} projects={projects} />
      <AboutBand home={pages.home} />
      <WorkAreaSection />
    </main>
  );
}
